import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  muscleKey,
  type MuscleAdjacencyWarning,
  type ProgramDraftWarning,
} from "@jackedlogs/shared";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";

// Split out of ProgramComposer for size (PE-21 pushed it past 350 lines) —
// a pure move, every value still owned/passed by ComposerBody. Two
// collapsible count+list blocks, same visual pattern, kept as SEPARATE
// sections rather than merged into one list because they're different
// classes of warning: `warnings` is client-computed draft-authoring
// validation (emptyCell/pctNoMax — incomplete cells, missing MAX), while
// `adjacencyWarnings` is server-resolved, about the SAVED program's muscle-
// recovery spacing (packages/api's programWarnings). The adjacency block
// renders ONLY the muscle name + a numeric pair count — never
// MuscleAdjacencyWarning.message (documented as an English, LLM-facing
// summary, not athlete-facing copy) or .days[].label (partly an unlocalized
// "Day N" fallback) — so nothing un-localized reaches a 5-locale UI.
export function ComposerWarningsPanel({
  theme,
  cs,
  warnings,
  warningLabel,
  warnOpen,
  setWarnOpen,
  adjacencyWarnings,
  adjacencyWarnOpen,
  setAdjacencyWarnOpen,
}: {
  theme: UiTheme;
  cs: ComposerStyles;
  warnings: readonly ProgramDraftWarning[];
  warningLabel: (w: ProgramDraftWarning) => string;
  warnOpen: boolean;
  setWarnOpen: (next: boolean | ((v: boolean) => boolean)) => void;
  adjacencyWarnings: readonly MuscleAdjacencyWarning[];
  adjacencyWarnOpen: boolean;
  setAdjacencyWarnOpen: (next: boolean | ((v: boolean) => boolean)) => void;
}) {
  const { t } = useTranslation();
  const p = theme.palette;

  if (warnings.length === 0 && adjacencyWarnings.length === 0) return null;

  return (
    <View style={{ gap: theme.spacing.s2 }}>
      {warnings.length > 0 ? (
        <View style={cs.checkSheet}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: warnOpen }}
            onPress={() => setWarnOpen((v) => !v)}
            style={cs.warnToggle}
          >
            <View style={cs.warnToggleRow}>
              <Icon name="warn" size={cs.checkTitle.fontSize} color={p.ink} />
              <Text style={cs.checkTitle}>
                {t("composer.warnLine", {
                  count: warnings.length,
                  defaultValue: "{{count}} warnings",
                })}
              </Text>
              <Icon
                name={warnOpen ? "chevron-up" : "chevron-down"}
                size={cs.checkTitle.fontSize}
                color={p.ink}
              />
            </View>
          </Pressable>
          {warnOpen
            ? warnings.map((w, i) => (
                <Text key={i} style={cs.checkItem}>
                  · {warningLabel(w)}
                </Text>
              ))
            : null}
        </View>
      ) : null}

      {adjacencyWarnings.length > 0 ? (
        <View style={cs.checkSheet}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: adjacencyWarnOpen }}
            onPress={() => setAdjacencyWarnOpen((v) => !v)}
            style={cs.warnToggle}
          >
            <View style={cs.warnToggleRow}>
              <Icon name="warn" size={cs.checkTitle.fontSize} color={p.ink} />
              <Text style={cs.checkTitle}>
                {t("composer.adjacencyWarnLine", {
                  count: adjacencyWarnings.length,
                  defaultValue: "{{count}} muscle-recovery warnings",
                })}
              </Text>
              <Icon
                name={adjacencyWarnOpen ? "chevron-up" : "chevron-down"}
                size={cs.checkTitle.fontSize}
                color={p.ink}
              />
            </View>
          </Pressable>
          {adjacencyWarnOpen
            ? adjacencyWarnings.map((w, i) => (
                <Text key={i} style={cs.checkItem}>
                  ·{" "}
                  {t("composer.adjacencyWarnItem", {
                    muscle: t(muscleKey(w.muscleSlug)),
                    count: w.adjacentPairs.length,
                    defaultValue: "{{muscle}} trained on adjacent days ({{count}}×)",
                  })}
                </Text>
              ))
            : null}
        </View>
      ) : null}
    </View>
  );
}
